import type { CurvaDocument } from "@/document/model/document";
import type { Camera } from "../camera";
import type { RenderClearColor, RenderOverlay, RenderPreview } from "../overlayTypes";
import { overlaySceneSignature } from "../sceneComposition";
import { displayItemVisualBounds, unionBounds } from "../scene/displayItemGeometry";
import type { DisplayItemDrawer } from "../scene/displayItemDrawer";
import { prepareSceneFrame, type PreparedSceneFrame } from "../scene/prepareSceneFrame";
import { RetainedScene } from "../scene/retainedScene";

export interface DocumentExportHost {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext;
  readonly renderTrianglesDebug: boolean;
  readonly displayItemDrawer: DisplayItemDrawer;
  setCamera(camera: Camera): void;
  resetRenderOverrides(): void;
  runExportComposition(sceneFrame: PreparedSceneFrame, clearColor?: RenderClearColor): void;
}

export function emptyExportOverlay(document: CurvaDocument): RenderOverlay {
  return {
    activePageId: document.pages[0]?.id,
    selectedNodeIds: [],
    previews: [],
  };
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Could not encode PNG"));
      }
    }, "image/png");
  });
}

export class DocumentExporter {
  // Exports render arbitrary, often-mutated documents — animation frames and
  // transitions are `structuredClone`s that deliberately keep the source
  // document's id and revision. The shared retained scene keys reuse by
  // id + revision + a bounds/visibility/geometry signature that intentionally
  // omits opacity, fills, strokes, effects, and rotation, so reusing it across
  // frames serves stale styling (e.g. a previous frame's opacity). Exports must
  // therefore render live: a dedicated retained scene, reset before every frame,
  // guarantees a fresh tessellation and never touches the shared scene.
  private readonly retainedScene = new RetainedScene();

  constructor(private readonly host: DocumentExportHost) {}

  async renderDocumentToPngBlob(
    document: CurvaDocument,
    fallbackBounds?: RenderPreview["bounds"],
    scale = 1,
  ): Promise<Blob> {
    await this.renderDocumentToCanvas(document, fallbackBounds, scale);
    return canvasToPngBlob(this.host.canvas);
  }

  async renderDocumentToCanvas(
    document: CurvaDocument,
    fallbackBounds?: RenderPreview["bounds"],
    scale = 1,
    options: { clearColor?: RenderClearColor } = {},
  ): Promise<void> {
    const overlay = emptyExportOverlay(document);
    // Reset per frame so no previous frame's tessellation is reused, then build
    // once so prepareSceneFrame below reuses this same fresh list.
    this.retainedScene.clear();
    const displayList = this.retainedScene.ensureDisplayList(document, { activePageId: overlay.activePageId });
    await this.host.displayItemDrawer.preloadDisplayListImages(displayList.items);
    const renderBounds = fallbackBounds ?? displayList.items.reduce(
      (bounds, item) => unionBounds(bounds, displayItemVisualBounds(item)),
      displayList.items[0]?.bounds ?? { x: 0, y: 0, width: 1, height: 1 },
    );
    const exportScale = Math.max(1, scale);
    const pixelWidth = Math.max(1, Math.ceil(renderBounds.width * exportScale));
    const pixelHeight = Math.max(1, Math.ceil(renderBounds.height * exportScale));

    this.host.canvas.width = pixelWidth;
    this.host.canvas.height = pixelHeight;
    this.host.setCamera({
      x: renderBounds.x,
      y: renderBounds.y,
      zoom: exportScale,
      devicePixelRatio: 1,
    });

    const sceneSignature = overlaySceneSignature(overlay, document.revision);
    const sceneFrame = prepareSceneFrame(document, overlay, sceneSignature, {
      renderTrianglesDebug: this.host.renderTrianglesDebug,
      retainedScene: this.retainedScene,
    });
    this.host.runExportComposition(sceneFrame, options.clearColor);
    this.host.gl.flush();
  }

  async renderDocumentThumbnail(
    document: CurvaDocument,
    contentBounds: RenderPreview["bounds"],
    boxSize: number,
  ): Promise<Blob> {
    this.retainedScene.clear();
    const displayList = this.retainedScene.ensureDisplayList(document);
    await this.host.displayItemDrawer.preloadDisplayListImages(displayList.items);
    const padding = 6;
    const available = boxSize - padding * 2;
    const scale = Math.min(
      available / Math.max(contentBounds.width, 1),
      available / Math.max(contentBounds.height, 1),
    );
    const viewWidth = boxSize / scale;
    const viewHeight = boxSize / scale;
    this.host.canvas.width = boxSize;
    this.host.canvas.height = boxSize;
    this.host.setCamera({
      x: contentBounds.x + contentBounds.width / 2 - viewWidth / 2,
      y: contentBounds.y + contentBounds.height / 2 - viewHeight / 2,
      zoom: scale,
      devicePixelRatio: 1,
    });

    const gl = this.host.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, boxSize, boxSize);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(1, 1, 1, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    this.host.resetRenderOverrides();
    this.host.displayItemDrawer.drawDisplayItems(displayList.items);
    gl.flush();

    return canvasToPngBlob(this.host.canvas);
  }
}
